import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Tag } from 'lucide-react'
import type { ProjectDocument } from '@/lib/db/rxdb/schemas/project'
import { LabelSelectionPanel } from '@/components/features/labels/components/LabelSelectionPanel'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  PICKER_SIDE_OFFSET,
  pickerPopoverClassName,
} from '@/components/app/combo-box/PickerPanel'
import { useTaskLabelMutations, useTaskLabels, useTasks } from '@/lib/db/rxdb/hooks'
import { useAllLabels } from '@/lib/hooks/useLabels'
import { useAllLabelGroups } from '@/lib/hooks/useLabelGroups'
import useLoginStore from '@/store/LoginStore'

interface BulkLabelPickerProps {
  taskIds: ReadonlySet<string>
  projects: Array<ProjectDocument>
  buttonClassName: string
  onApplied: () => void
}

/**
 * Adds labels to every selected task. Add-only: a task keeps the labels it has,
 * except that a picked label replaces its sibling in the same label group.
 * Project labels are offered only when all selected tasks share that project.
 */
export function BulkLabelPicker({ taskIds, projects, buttonClassName, onApplied }: BulkLabelPickerProps) {
  const { t } = useTranslation('tasks')
  const workspaceId = useLoginStore((state) => state.workspaceId)
  const { labels } = useAllLabels(workspaceId)
  const { labelGroups } = useAllLabelGroups(workspaceId)
  const { tasks } = useTasks()
  const { taskLabels } = useTaskLabels()
  const { createTaskLabel, deleteTaskLabel } = useTaskLabelMutations()
  const [open, setOpen] = useState(false)
  const [pickedIds, setPickedIds] = useState<Array<string>>([])

  const selectedTasks = useMemo(() => tasks.filter((task) => taskIds.has(task.id)), [tasks, taskIds])
  const sharedProjectId = useMemo(() => {
    const projectIds = new Set(selectedTasks.map((task) => task.projectId ?? null))
    return projectIds.size === 1 ? [...projectIds][0] : null
  }, [selectedTasks])
  const availableLabels = useMemo(
    () => labels.filter((label) => !label.projectId || label.projectId === sharedProjectId),
    [labels, sharedProjectId],
  )

  const handleOpenChange = (next: boolean) => {
    setOpen(next)
    if (!next) setPickedIds([])
  }

  const apply = () => {
    const labelsById = new Map(labels.map((label) => [label.id, label]))
    for (const task of selectedTasks) {
      const current = taskLabels.filter((taskLabel) => taskLabel.taskId === task.id)
      for (const labelId of pickedIds) {
        const label = labelsById.get(labelId)
        if (!label || current.some((taskLabel) => taskLabel.labelId === labelId)) continue
        if (label.labelGroupId) {
          for (const sibling of current) {
            if (labelsById.get(sibling.labelId)?.labelGroupId === label.labelGroupId) {
              void deleteTaskLabel(sibling.id)
            }
          }
        }
        void createTaskLabel({
          taskId: task.id,
          labelId: label.id,
          labelName: label.name,
          labelColor: label.color,
          labelGroupId: label.labelGroupId ?? null,
        })
      }
    }
    handleOpenChange(false)
    onApplied()
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger className={buttonClassName}>
        <Tag className="size-3.5" />
        {t('bulk.labels')}
      </PopoverTrigger>
      <PopoverContent side="top" align="start" sideOffset={PICKER_SIDE_OFFSET} className={pickerPopoverClassName}>
        <LabelSelectionPanel
          availableLabels={availableLabels}
          labelGroups={labelGroups}
          projects={projects}
          selectedLabelIds={pickedIds}
          onSelectionChange={setPickedIds}
          projectId={sharedProjectId}
        />
        <div className="border-t border-border p-1.5">
          <button
            type="button"
            onClick={apply}
            disabled={pickedIds.length === 0}
            className="flex h-8 w-full items-center justify-center rounded-md bg-brand text-[13px] font-semibold text-brand-foreground transition-opacity hover:opacity-90 disabled:pointer-events-none disabled:opacity-40"
          >
            {t('bulk.addLabels', { count: selectedTasks.length })}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
